import type { TikTokAdRequestStatus } from "@/services/tiktokShop.service";
import axios from "axios";

import { formatCompactCount } from "@/utils/formatters";

/** A whole tab that cannot load, and why. */
export type TikTokBlock =
  | { kind: "not_connected" }
  | { kind: "reconnect" }
  | { kind: "awaiting_approval" }
  | { kind: "error"; message: string };

const responseCode = (error: unknown): string | undefined =>
  axios.isAxiosError(error) ? (error.response?.data as { code?: string } | undefined)?.code : undefined;

export function blockFromError(error: unknown): TikTokBlock {
  const code = responseCode(error);
  if (code === "TIKTOK_SHOP_NOT_CONNECTED") return { kind: "not_connected" };
  if (code === "INTEGRATION_RECONNECT_REQUIRED") return { kind: "reconnect" };
  if (code === "TIKTOK_SCOPE_MISSING") return { kind: "awaiting_approval" };
  return { kind: "error", message: errorMessage(error) };
}

/** True when an action failed because the whole feature is unavailable. */
export function isBlockingError(error: unknown): boolean {
  return blockFromError(error).kind !== "error";
}

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === "string") return data.message;
    if (Array.isArray(data?.message)) return data.message.join(", ");
    return error.message || "Something went wrong.";
  }
  return "Something went wrong.";
}

export const usd = (n: number) =>
  n >= 10_000 ? `$${formatCompactCount(n)}` : `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

/** "private" for a figure the creator does not disclose. */
export const orPrivate = (value: number | null, format: (n: number) => string) =>
  value === null ? "private" : format(value);

export function timeAgo(iso: string | null): string {
  if (!iso) return "never";
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** Time left until a deadline, e.g. "2d 4h" or "45m"; null once it passed. */
export function timeLeft(iso: string | null): { label: string; urgent: boolean } | null {
  if (!iso) return null;
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return null;
  const hours = ms / 3_600_000;
  const label =
    hours >= 48 ? `${Math.floor(hours / 24)}d ${Math.floor(hours % 24)}h` : hours >= 1 ? `${Math.floor(hours)}h` : `${Math.max(1, Math.floor(ms / 60_000))}m`;
  return { label, urgent: hours < 24 };
}

export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export const SAMPLE_STAGE: Record<string, { label: string; step: number }> = {
  AWAITING_SHIPMENT: { label: "Approved, waiting to ship", step: 1 },
  SHIPPED: { label: "Shipped", step: 2 },
  CONTENT_PENDING: { label: "Delivered, waiting for content", step: 3 },
  COMPLETED: { label: "Content posted", step: 4 },
  OPS_COMPLETED: { label: "Marked complete by TikTok", step: 4 },
};

export const CANCELLED_LABEL: Record<string, string> = {
  REJECT_CANCELLED: "You rejected it",
  OVERDUE_CANCELLED: "Not reviewed in time",
  UNFULFILL_CANCELLED: "Creator did not post",
  DEL_OPEN_COLLAB: "Open collaboration removed",
  SELLER_NOT_SHIP_CANCELLED: "Not shipped in time",
  WITHDRAW_CANCELLED: "Creator withdrew",
  UNFULFILLABLE_CANCELLED: "Creator could not post",
  OPS_CANCELLED: "Cancelled by TikTok",
  OPS_FAILED: "Marked failed by TikTok",
};

export const REJECT_REASON_LABEL = {
  NOT_MATCH: "Not a fit for this product",
  OUT_OF_STOCK: "Out of stock",
  OFFLINE: "Product taken offline",
  OTHER: "Other reason",
} as const;

export const AD_REQUEST_STATUS_LABEL: Record<TikTokAdRequestStatus, string> = {
  REQUESTED: "Sent",
  ACCEPTED: "Accepted",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  LAUNCHED: "Running as an ad",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
};
